<template>
    <div class="flex gap-4" v-if="!readOnly">
        <activix-tooltip :content="addingToSalesJourneyTooltip" v-if="isAttributionContext">
            <div>
                <activix-button
                    size="small"
                    :disabled="addingToSalesJourneyDisabled"
                    :loading="addingToSalesJourneyInProgress"
                    :type="selectedForViewing ? 'primary' : 'default'"
                    @click.stop="$emit('add-to-sales-journey')"
                >
                    {{ $t('addLead.addToSalesJourney') }}
                </activix-button>
            </div>
        </activix-tooltip>

        <activix-button
            class="grow"
            size="small"
            :type="selectedForViewing ? 'primary' : 'default'"
            @click.stop="$emit('complete')"
            v-else-if="showCompleteButton"
        >
            {{ hasCompletableValues ? $t('addLead.complete') : $t('addLead.select') }}
        </activix-button>

        <activix-tooltip :content="associationTooltip">
            <div class="flex grow">
                <activix-button
                    class="grow"
                    size="small"
                    :loading="associationInProgress"
                    :disabled="associationDisabled"
                    :type="selectedForViewing ? 'primary' : 'default'"
                    @click.stop="$emit('associate')"
                >
                    {{ $t('addLead.associate') }}
                </activix-button>
            </div>
        </activix-tooltip>
    </div>
</template>

<script>
    /* eslint-disable vue/require-prop-types */

    import Account from '@/entities/Account.js';
    import Division from '@/entities/Division.js';
    import LeadType from '@/entities/LeadType.js';

    export default {
        inject: ['$association', '$attribution'],

        emits: ['add-to-sales-journey', 'complete', 'associate'],

        props: {
            isAttributionContext: {
                type: Boolean,
                default: false,
            },
            leadResultDivision: {
                type: Number,
                required: false,
            },
            leadResultType: {
                type: Number,
                required: true,
            },
            newLeadDivision: {
                type: Number,
                required: false,
            },
            selectedForViewing: {
                required: true,
            },
            hasCompletableValues: {
                required: true,
            },
            account: {
                type: Account,
                required: true,
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            addingToSalesJourneyDisabled() {
                if (this.$attribution().disabled) {
                    return true;
                }
                if (this.addingToSalesJourneyTypeRenewal) {
                    return true;
                }
                if (this.account.isPolestar()) {
                    return this.addingToSalesJourneyDivisionMismatch;
                }

                return this.addingToSalesJourneyDivisionService;
            },

            addingToSalesJourneyDivisionMismatch() {
                return this.leadResultDivision && this.newLeadDivision !== this.leadResultDivision;
            },

            addingToSalesJourneyDivisionService() {
                return this.leadResultDivision === Division.SERVICE;
            },

            addingToSalesJourneyTypeRenewal() {
                return this.leadResultType === LeadType.RENEWAL;
            },

            addingToSalesJourneyInProgress() {
                if (!this.selectedForViewing) {
                    return false;
                }

                return this.$attribution().creating;
            },

            addingToSalesJourneyTooltip() {
                if (this.$attribution().tooltip) {
                    return this.$attribution().tooltip;
                }

                if (this.addingToSalesJourneyTypeRenewal) {
                    return this.$t('addLead.addToSalesJourneyDisabledByTypeRenewalTooltip');
                }

                if (this.account.isPolestar() && this.addingToSalesJourneyDivisionMismatch) {
                    return this.$t('addLead.addToSalesJourneyDisabledByDivisionTooltip');
                }

                if (this.addingToSalesJourneyDivisionService) {
                    return this.$t('addLead.addToSalesJourneyDisabledByDivisionServiceTooltip');
                }

                return '';
            },

            showCompleteButton() {
                return !this.$association().leadId;
            },

            associationDisabled() {
                if (!this.selectedForViewing) {
                    return false;
                }

                return this.$association().disabled;
            },

            associationTooltip() {
                if (!this.selectedForViewing) {
                    return '';
                }

                return this.$association().tooltip;
            },

            associationInProgress() {
                if (!this.selectedForViewing) {
                    return false;
                }

                return this.$association().creating;
            },
        },
    };
</script>
